import React from 'react'
import { useState, forwardRef, useImperativeHandle } from 'react'
import moment, { Moment } from 'moment'
import styles from './style.module.scss'
import MonthGrid from '../MonthGrid/index'

import navigationRight16SVG from 'assets/images/navigation-right-16.svg'
import navigationLeft16SVG from 'assets/images/navigation-left-16.svg'
import chevronCircleRight24SVG from 'assets/images/chevron-circle-right-24.svg'
import chevronCircleDown24SVG from 'assets/images/chevron-circle-down-24.svg'

interface CalendarNavigationState {
  focusedDate: Moment
  browseDate: Moment
  expanded: boolean
}

interface CalendarNavigationProps {
  focusedDate: Date
  onDateChange: (date: Date) => void
  onMonthChange?: ((date: Date) => void) | null
  viewType?: 'month' | 'week' | 'day'
  disableExpand?: boolean
  hasEventIndexes?: number[] | null
}

export interface CalendarNavigationRef {
  goToday: () => void
}

const CalendarNavigation = forwardRef<CalendarNavigationRef, CalendarNavigationProps>(
  (
    {
      focusedDate,
      onDateChange,
      onMonthChange = null,
      viewType = 'month',
      disableExpand = false,
      hasEventIndexes = null,
    },
    ref,
  ) => {
    const [state, setState] = useState<CalendarNavigationState>({
      focusedDate: moment(focusedDate).startOf('day'),
      browseDate: moment(focusedDate).startOf('day'),
      expanded: true,
    })

    const aDate = moment(focusedDate).startOf('day')
    if (!state.focusedDate.isSame(aDate, 'day')) {
      setState({
        ...state,
        focusedDate: aDate,
        browseDate: aDate,
      })
    }

    const handleFocusedDateChange = (date: Moment): void => {
      if (!date.isSame(state.browseDate, 'month')) {
        setState({ ...state, browseDate: date, focusedDate: date })
      } else {
        setState({ ...state, focusedDate: date })
      }
      onDateChange(date.toDate())
    }

    const handleDateChange = (date: Moment): void => {
      setState({ ...state, browseDate: date })
      onMonthChange?.(date.toDate())
    }

    const mDate = moment(state.browseDate)
    const yIndex = mDate.localeData().longDateFormat('LL').indexOf('YYYY')
    let monthStr = ''
    if (yIndex === 0) {
      monthStr = mDate.format('YYYY MMMM')
    } else {
      monthStr = mDate.format('MMMM YYYY')
    }

    const handlePrevMonth = (): void => {
      const newDate = moment(state.browseDate).subtract(1, 'month')
      handleDateChange(newDate)
    }

    const handleNextMonth = (): void => {
      const newDate = moment(state.browseDate).add(1, 'month')
      handleDateChange(newDate)
    }

    const goToday = (): void => {
      const today = moment().startOf('day')
      setState({
        ...state,
        browseDate: today,
        focusedDate: today,
      })
      onDateChange(today.toDate())
    }

    useImperativeHandle(ref, () => ({
      goToday,
    }))

    return (
      <div className={styles.wrapper}>
        <div className={styles.container}>
          <div className={styles.navigationGroup}>
            {!disableExpand && (
              <button
                className={styles.expandButton}
                onClick={() => setState({ ...state, expanded: !state.expanded })}
              >
                <img
                  src={state.expanded ? chevronCircleDown24SVG : chevronCircleRight24SVG}
                  alt="navigation-expand"
                />
              </button>
            )}
            <div className={styles.monthText}>{monthStr}</div>
          </div>
          <div className={styles.arrowButtonGroup}>
            <button className={styles.arrowButton} onClick={handlePrevMonth}>
              <img src={navigationLeft16SVG} alt="navigation-left" />
            </button>
            <button className={styles.arrowButton} onClick={handleNextMonth}>
              <img src={navigationRight16SVG} alt="navigation-right" />
            </button>
          </div>
        </div>
        {state.expanded && (
          <div className={styles.monthGridContainer}>
            <MonthGrid
              date={state.browseDate as any}
              focusedDate={state.focusedDate as any}
              viewType={viewType}
              onDateClick={handleFocusedDateChange}
              hasEventIndexes={hasEventIndexes as any}
            />
          </div>
        )}
      </div>
    )
  },
)

export default CalendarNavigation
